import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface DateInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: Date) => void;
}

const DateInputModal: React.FC<DateInputModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    // 오늘 날짜를 YYYY-MM-DD 형식으로 설정
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // 모달이 열릴 때마다 오늘 날짜로 초기화
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}`);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selectedDate) {
      alert('날짜를 선택해주세요.');
      return;
    }

    const date = new Date(selectedDate);
    if (isNaN(date.getTime())) {
      alert('올바른 날짜를 입력해주세요.');
      return;
    }

    onConfirm(date);
    onClose();
  };

  const handleCancel = () => {
    setSelectedDate('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={handleCancel} />
      <ModalContainer>
        <ModalContent>
          <ModalTitle>추가 날짜 선택</ModalTitle>
          <DateInputWrap>
            <DateInput
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
            />
          </DateInputWrap>
          <ButtonContainer>
            <CancelButton data-variant="modal-secondary" onClick={handleCancel}>
              취소
            </CancelButton>
            <ConfirmButton onClick={handleConfirm}>
              확인
            </ConfirmButton>
          </ButtonContainer>
        </ModalContent>
      </ModalContainer>
    </>
  );
};

// 스타일 컴포넌트들
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 30000;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 30001;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: min(420px, calc(100vw - 32px));
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
`;

const ModalTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
`;

const DateInputWrap = styled.div`
  width: 100%;
  min-width: 0;
  margin-bottom: 20px;
`;

const DateInput = styled.input`
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default DateInputModal; 